export const CORPORATE_DOMAIN = "krivamail.com";
export const CORPORATE_SUFFIX = "@krivamail.com";
export const BYTES_UNITS = ["B", "KB", "MB", "GB", "TB"];

export function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function plainTextToHtml(value = "") {
  return escapeHtml(value).replace(/\r?\n/g, "<br>");
}

export function isLikelyHtml(value = "") {
  return /<[a-z][\s\S]*>/i.test(value);
}

export function toEditorHtml(value = "") {
  if (!value) return "";
  return isLikelyHtml(value) ? value : plainTextToHtml(value);
}

export function buildReplySubject(subject = "") {
  const trimmed = subject.trim();
  if (!trimmed) return "Re:";
  return trimmed.toLowerCase().startsWith("re:") ? trimmed : `Re: ${trimmed}`;
}

export function buildForwardSubject(subject = "") {
  const trimmed = subject.trim();
  if (!trimmed) return "Fwd:";
  return trimmed.toLowerCase().startsWith("fwd:") ? trimmed : `Fwd: ${trimmed}`;
}

export function extractEmailAddress(value = "") {
  if (typeof value !== "string") return "";
  let candidate = value.trim();
  if (!candidate) return "";

  const angleMatch = candidate.match(/<([^<>]+)>/);
  if (angleMatch) {
    candidate = angleMatch[1];
  }

  candidate = candidate.replace(/^"|"$/g, "").replace(/^'|'$/g, "").trim();
  return candidate;
}

export function isCorporateEmail(email = "") {
  const normalized = extractEmailAddress(email).toLowerCase();
  return normalized.endsWith(CORPORATE_SUFFIX);
}

export function isValidEmail(value = "") {
  if (typeof value !== "string") return false;
  const normalized = extractEmailAddress(value).toLowerCase();
  if (!normalized) return false;
  // General email validation regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

export function parseEmailList(value = "") {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map(email => extractEmailAddress(email.trim()))
    .filter(email => email.length > 0);
}

export function extractRecipientEmails(value = []) {
  const items = Array.isArray(value) ? value : [value];
  const emails = [];

  items.forEach((entry) => {
    if (!entry) return;

    if (typeof entry === "string") {
      const parsed = parseEmailList(entry).filter(isValidEmail);
      emails.push(...parsed);
      return;
    }

    if (typeof entry === "object") {
      const direct = entry.address || entry.email || entry.value || entry.mail || entry.recipient;
      if (typeof direct === "string") {
        const parsed = parseEmailList(direct).filter(isValidEmail);
        emails.push(...parsed);
      }
      return;
    }
  });

  return Array.from(new Set(emails.filter(Boolean)));
}

export function collectReplyAllRecipients(mail = {}, currentUserEmail = "") {
  if (!mail || typeof mail !== "object") {
    return { to: [], cc: [], bcc: [] };
  }

  const myEmail = extractEmailAddress(currentUserEmail || "").toLowerCase();

  const toList = extractRecipientEmails(mail.to || []).filter(
    (email) => email.toLowerCase() !== myEmail
  );
  const ccList = extractRecipientEmails(mail.cc || []).filter(
    (email) => email.toLowerCase() !== myEmail
  );
  const bccList = extractRecipientEmails(mail.bcc || []).filter(
    (email) => email.toLowerCase() !== myEmail
  );

  const originalSender = (() => {
    if (typeof mail.from === "string") return extractEmailAddress(mail.from);
    if (mail.from?.address) return extractEmailAddress(mail.from.address);
    if (mail.fromEmail) return extractEmailAddress(mail.fromEmail);
    return "";
  })();

  return {
    to: toList.length > 0 ? toList : (originalSender ? [originalSender] : []),
    cc: ccList,
    bcc: bccList,
  };
}

export function validateEmailList(value = "") {
  const emails = parseEmailList(value);
  const invalidEmails = emails.filter(email => !isValidEmail(email));
  return {
    emails,
    invalidEmails,
    isValid: invalidEmails.length === 0 && emails.length > 0
  };
}

export function getLastEmailFragment(value = "") {
  if (typeof value !== "string") return "";
  const parts = value.split(",");
  return parts[parts.length - 1].trim();
}

export function replaceLastEmailFragment(value = "", replacement = "") {
  if (typeof value !== "string") return replacement;
  const parts = value.split(",");
  parts[parts.length - 1] = replacement;
  return parts.join(",");
}

export function formatBytes(input) {
  const value = Number(input);
  if (!Number.isFinite(value) || value <= 0) return "";
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < BYTES_UNITS.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = size >= 10 || unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${BYTES_UNITS[unitIndex]}`;
}

export function resolveAttachmentHref(baseUrl, attachmentPath) {
  if (!attachmentPath) return "";
  if (/^https?:/i.test(attachmentPath)) {
    return attachmentPath;
  }
  if (!baseUrl) {
    return attachmentPath;
  }
  const normalized = attachmentPath.startsWith("/") ? attachmentPath : `/${attachmentPath}`;
  return `${baseUrl}${normalized}`;
}

export function getUsername(email = "") {
  if (!email) return "";
  return email.split("@")[0];
}

export function getDisplayName(fromString = "", fallbackFullName = "") {
  if (fallbackFullName) return fallbackFullName;

  if (!fromString) return "";

  // Case: "Full Name <email@domain.com>"
  const nameMatch = fromString.match(/^([^<]+)</);
  if (nameMatch) {
    return capitalizeWords(nameMatch[1].trim());
  }

  // Case: email only
  const username = fromString.split("@")[0];

  return capitalizeWords(username.replace(/\./g, " "));
}

function capitalizeWords(value = "") {
  return value
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
